import { useCallback, useEffect, useRef, useState } from 'react';

interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type SpeechWindow = Window & {
  SpeechRecognition?: new () => Recognition;
  webkitSpeechRecognition?: new () => Recognition;
};

/** Dictado editable; nunca envía la receta ni la guarda al terminar de hablar. */
export function useDictation(locale: string) {
  const browser = typeof window === 'undefined' ? undefined : window as SpeechWindow;
  const Constructor = browser?.SpeechRecognition ?? browser?.webkitSpeechRecognition;
  const recognition = useRef<Recognition | null>(null);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string>();

  const stop = useCallback(() => recognition.current?.stop(), []);
  const start = useCallback(() => {
    if (!Constructor || recognition.current) return;
    const current = new Constructor();
    recognition.current = current;
    current.lang = locale;
    current.continuous = true;
    current.interimResults = true;
    current.onresult = ({ results }) => setTranscript(Array.from(results, (result) => result[0].transcript).join(' ').trim());
    current.onerror = ({ error: reason }) => {
      setError(reason === 'not-allowed' ? 'Activa el permiso del micrófono o escribe la receta.' : 'No se ha podido completar el dictado. Puedes corregir el texto.');
      setListening(false);
    };
    current.onend = () => { recognition.current = null; setListening(false); };
    setTranscript('');
    setError(undefined);
    try { current.start(); setListening(true); }
    catch { recognition.current = null; setListening(false); setError('No se ha podido iniciar el dictado. Puedes escribir la receta.'); }
  }, [Constructor, locale]);

  useEffect(() => () => {
    const current = recognition.current;
    if (current) { current.onresult = null; current.onend = null; current.onerror = null; current.abort(); }
    recognition.current = null;
  }, []);

  return { supported: Boolean(Constructor), listening, transcript, start, stop, error };
}
